import React, {
    Dispatch,
    SetStateAction,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import Descriptor from '@r6digital/rapidstor-types/react/entities/Descriptor';
import Location from '@r6digital/rapidstor-types/react/entities/Location';
import UnitType from '@r6digital/rapidstor-types/react/entities/UnitType';

import { RapidStorSettings } from '@/utils/rapidstor';

import { sortUnitTypes } from '../../features/rapidstor/helpers';
import {
    DescriptorOptions,
    unitOptionsTransformer,
    unitTypeNextSizeUp,
    UpgradeDescriptor,
} from '../../features/rapidstor/UnitOptions';
import {
    ANIMATION_DELAY,
    ANIMATION_SPEED,
    optionsCardVariants,
} from '@/features/rapidstor/animations';
import { JourneyContext } from '@/features/rapidstor/providers/JourneyProvider';

import classes from './OptionSelector.module.scss';

import { formatCurrency } from '../../utils/currency';
import { FormType } from '../elements/contact-card';
import Coupon from '../rapidstor/coupon';
import ContactCard from './contact-card';
import OptionsCard from './options-card';
import SelectedCard from './selected-card';
import UpsellCard from './upsell-card';

interface Props {
    descriptor: Descriptor;
    unitType: UnitType;
    location: Location;
    upSell: Descriptor | null;
    deal: string | null;
    desktop?: boolean;
    reservationToken?: string | null;
    toggleContactCard: (type: FormType) => void;
    makeReservation: () => void;
    loading: boolean;
    setLoading: Dispatch<SetStateAction<boolean>>;
    selectedID: string;
    setSelectedID: Dispatch<SetStateAction<string>>;
}

function OptionSelector({
    descriptor,
    unitType,
    location,
    upSell,
    deal,
    desktop,
    makeReservation,
    reservationToken,
    loading,
    setLoading,
    selectedID,
    setSelectedID,
}: Props) {
    const {
        unitData,
        selectedDescriptor,
        selectedUnitType,
        setSelectedUnitType,
        reservationData,
    } = useContext(JourneyContext);
    const useDescriptorOptions = DescriptorOptions(descriptor, unitData?.descriptors ?? [], location);

    const [upSellUnitType, setUpSellUnitType] = useState<UnitType | undefined>();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [descriptorUpsells, setDescriptorUpsells] = useState<UpgradeDescriptor[]>([]);
    const [unitTypeUpsells, setUnitTypeUpsells] = useState<UnitType[]>([]);

    useEffect(() => {
        if (!selectedDescriptor || !selectedUnitType || !reservationData) {
            navigate('/');
            return;
        }

        if (useDescriptorOptions.isDescriptorUpsell()) {
            setDescriptorUpsells(
                useDescriptorOptions.getDescriptorUpsells(unitData?.descriptors ?? [])
            );
        } else {
            setUnitTypeUpsells(
                useDescriptorOptions
                    .getUnitTypeUpsells()
                    .filter((unitType) => unitType._id !== reservationData.unitType._id)
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDescriptor, selectedID, selectedUnitType]);

    useEffect(() => {
        setSelectedUnitType(unitType);

        if (upSell) {
            const upSellOptions: UnitType[] = unitOptionsTransformer(upSell);
            const sortedUpSellOptions = sortUnitTypes(upSellOptions, location);
            setUpSellUnitType(sortedUpSellOptions[0]);
        }
    }, [descriptor, location, setSelectedUnitType, unitType, upSell]);

    const selectedUnitRate = useMemo(() => {
        return deal ? unitType.rates.discountedRate : unitType.rates.rate;
    }, [deal, unitType.rates.discountedRate, unitType.rates.rate]);

    const upSellDifference = useMemo(() => {
        const upSellRate = deal
            ? upSell?.unitTypes[0].rates.discountedRate
            : upSell?.unitTypes[0].rates.rate;
        if (!selectedUnitRate || !upSellRate) {
            return 0;
        }
        return upSellRate - selectedUnitRate;
    }, [deal, selectedUnitRate, upSell?.unitTypes]);

    const hasMultipleOptions = useMemo(() => {
        if (useDescriptorOptions.isDescriptorUpsell()) {
            return descriptorUpsells.length >= 1 && descriptorUpsells.some((descriptor) => descriptor.unitTypes.find((unitType) => unitType.hasComputedAvailable));
        } else {
            return unitTypeUpsells.length > 0 && unitTypeUpsells.some((unitType) => unitType.hasComputedAvailable);
        }
    }, [descriptorUpsells, unitTypeUpsells, useDescriptorOptions]);

    return (
        <>
            {unitType && (
                <div className={`${classes.card_wrapper}`}>
                    <SelectedCard
                        deal={deal ?? null}
                        desktop={desktop === true}
                        descriptor={descriptor}
                        descriptorOptions={descriptorUpsells}
                        unitTypeOptions={unitTypeUpsells}
                        makeReservation={makeReservation}
                        reservationToken={reservationToken ?? null}
                        selectedID={selectedID}
                        setSelectedID={setSelectedID}
                        loading={loading}
                        setLoading={setLoading}
                    />

                    {upSell &&
                        upSellUnitType &&
                        unitData &&
                        unitTypeNextSizeUp(descriptor.area, unitData.descriptors) ? (
                        <motion.div
                            layout={true}
                            variants={optionsCardVariants}
                            initial={'start'}
                            animate={'show'}
                            transition={{
                                duration: ANIMATION_SPEED,
                                delay: ANIMATION_DELAY,
                            }}
                            className={`${classes.upsell_copy} ${!desktop && classes.mobile
                                }`}
                            style={{ margin: desktop ? '15% auto' : '' }}
                        >
                            <span className={`${classes.or}`}>{t('labels:or')}</span>
                            <span className={`${classes.price_difference}`}>
                                {Math.sign(upSellDifference) === -1
                                    ? `Upsize and save ${formatCurrency(
                                        upSellDifference,
                                        location
                                    )}`
                                    : Math.sign(upSellDifference) === 0
                                        ? `Upsize for no price difference`
                                        : `Upsize for an extra ${formatCurrency(
                                            upSellDifference,
                                            location
                                        )}`}
                            </span>
                        </motion.div>
                    ) : (
                        <motion.div
                            layout={true}
                            variants={optionsCardVariants}
                            initial={'start'}
                            animate={'show'}
                            transition={{
                                duration: ANIMATION_SPEED,
                                delay: ANIMATION_DELAY,
                            }}
                            className={`${classes.upsell_copy} ${desktop ? classes.desktop : classes.mobile
                                }`}
                        >
                            <span>{t('labels:or')}</span>
                        </motion.div>
                    )}

                    {upSell &&
                        upSellUnitType &&
                        !hasMultipleOptions &&
                        upSell.area != unitType.area ? (
                        <UpsellCard
                            descriptor={upSell}
                            unitType={upSellUnitType}
                            priceDifference={upSellDifference}
                            desktop={desktop === true}
                            loading={loading}
                            setLoading={setLoading}
                        />
                    ) : (
                        <>
                            {hasMultipleOptions && desktop === true ? (
                                <>
                                    <OptionsCard
                                        descriptor={descriptor}
                                        descriptorOptions={descriptorUpsells}
                                        unitTypeOptions={unitTypeUpsells}
                                        selectedID={selectedID}
                                        setSelectedID={setSelectedID}
                                        desktop
                                        loading={loading}
                                        setLoading={setLoading}
                                        initialRate={selectedUnitRate}
                                    />
                                </>
                            ) : (
                                <ContactCard
                                    location={location}
                                    loading={loading}
                                    desktop={desktop === true}
                                />
                            )}
                        </>
                    )}
                    {RapidStorSettings(location).showDealCoupons() &&
                        !desktop &&
                        !hasMultipleOptions && <Coupon type='base' />}
                </div>
            )}
        </>
    );
}

export default OptionSelector;
